import { useState, type FormEvent } from 'react'
import { Link, router } from '@inertiajs/react'
import AccountLayout from '@/layouts/AccountLayout'
import PageHeader from '@/components/PageHeader'
import { useAccountPath } from '@/lib/useAccountPath'

interface TemplateItem {
  id: number
  name: string
  description: string | null
  question_count: number
  survey_count: number
  created_at: string
}

interface TemplatesProps {
  templates: TemplateItem[]
}

export default function Templates({ templates }: TemplatesProps) {
  const ap = useAccountPath()
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)

  function handleCreate(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    router.post(ap('/survey_templates'), { survey_template: { name: name.trim(), description: description.trim() || null } }, {
      onSuccess: () => { setName(''); setDescription(''); setShowForm(false); setSubmitting(false) },
      onError: () => setSubmitting(false),
    })
  }

  function handleDuplicate(id: number) {
    router.post(ap(`/survey_templates/${id}/duplicate`))
  }

  function handleDelete(id: number) {
    if (!confirm('Delete this template?')) return
    router.delete(ap(`/survey_templates/${id}`))
  }

  return (
    <AccountLayout>
      <div className="space-y-6">
        <PageHeader
          title="Templates"
          actions={!showForm ? (
            <button onClick={() => setShowForm(true)} className="btn btn-primary btn-sm">
              <span className="iconify lucide--plus size-4" />
              New Template
            </button>
          ) : undefined}
        />

        {showForm && (
          <form onSubmit={handleCreate} className="card bg-base-100">
            <div className="card-body space-y-3">
              <h3 className="font-medium">New template</h3>
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Name</legend>
                <input type="text" className="input w-full" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Post-Workshop Feedback" required autoFocus />
              </fieldset>
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Description (optional)</legend>
                <textarea className="textarea w-full" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description of what this template measures..." />
              </fieldset>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={submitting || !name.trim()}>
                  {submitting && <span className="loading loading-spinner loading-sm" />}
                  Create
                </button>
              </div>
            </div>
          </form>
        )}

        <div className="space-y-3">
          {templates.map((t) => (
            <div key={t.id} className="card bg-base-100 group">
              <div className="card-body py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold">{t.name}</h2>
                      <span className="badge badge-ghost badge-sm">{t.question_count} questions</span>
                      {t.survey_count > 0 && (
                        <span className="badge badge-sm badge-soft badge-primary">{t.survey_count} surveys</span>
                      )}
                    </div>
                    {t.description && (
                      <p className="text-sm text-base-content/60 mt-1">{t.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Link href={ap(`/survey_templates/${t.id}/edit`)} className="btn btn-square btn-ghost btn-sm" title="Edit">
                      <span className="iconify lucide--pencil size-4" />
                    </Link>
                    <button onClick={() => handleDuplicate(t.id)} className="btn btn-square btn-ghost btn-sm" title="Duplicate">
                      <span className="iconify lucide--copy size-4" />
                    </button>
                    <button onClick={() => handleDelete(t.id)} className="btn btn-square btn-ghost btn-sm text-error" title="Delete" disabled={t.survey_count > 0}>
                      <span className="iconify lucide--trash-2 size-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {templates.length === 0 && (
            <div className="card bg-base-100">
              <div className="card-body items-center text-center py-12">
                <span className="iconify lucide--file-text size-12 text-base-content/20 mb-3" />
                <h2 className="text-lg font-semibold">No templates</h2>
                <p className="text-base-content/50 text-sm">Create a template to define reusable question sets.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </AccountLayout>
  )
}
